import React, { useId } from "react";
import { twMerge } from "tailwind-merge";

export type SwitchSize = "sm" | "md";

export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  id?: string;
  disabled?: boolean;
  label?: React.ReactNode;
  labelPlacement?: "right" | "left";
  size?: SwitchSize;
  className?: string;
  "aria-label"?: string;
}

const SIZE: Record<SwitchSize, string> = {
  md: "h-6 w-11 after:h-5 after:w-5",
  sm: "h-5 w-9 after:h-4 after:w-4",
};

export const Switch: React.FC<SwitchProps> = ({
  checked,
  onChange,
  id,
  disabled = false,
  label,
  labelPlacement = "right",
  size = "md",
  className,
  "aria-label": ariaLabel,
}) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  const track = twMerge(
    "relative inline-flex shrink-0 rounded-full bg-gray-200 transition-colors",
    "after:absolute after:left-0.5 after:top-0.5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-['']",
    "peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white",
    "peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500 peer-focus-visible:ring-offset-2",
    "peer-disabled:cursor-not-allowed peer-disabled:opacity-50",
    SIZE[size],
    className
  );

  const labelNode =
    label != null ? (
      <span className="select-none text-sm font-medium text-gray-700">
        {label}
      </span>
    ) : null;

  return (
    <label
      htmlFor={inputId}
      className={twMerge(
        "inline-flex items-center gap-2",
        disabled ? "cursor-not-allowed" : "cursor-pointer"
      )}
    >
      {labelPlacement === "left" && labelNode}
      <input
        id={inputId}
        type="checkbox"
        role="switch"
        className="peer sr-only"
        checked={checked}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className={track} />
      {labelPlacement === "right" && labelNode}
    </label>
  );
};

export default Switch;
